import {useEffect, useState} from 'react'
import axios from 'axios'
import {ReverseByDatePanel} from './VnRevertByDate'

const API_URL = process.env.REACT_APP_API_URL

interface VnStats {
  total: number
  answered: number
  missed: number
  duration: number
  capped?: boolean
}

interface CallRow {
  date: string
  phone: string
  duration: number
  answered: boolean
}

interface MyVirtualNumber {
  ddi: string
  forward_number: string
  stats: VnStats
  calls: CallRow[]
  whatsapp_leads: number
  period: {from: string; to: string}
}

interface WhatsappLead {
  id: string
  created_at: string
  lead_name: string
  lead_phone: string
  message: string
  listing_code: string
  listing_text: string
  listing_image: string
  listing_price: number | null
  listing_deleted?: boolean
  matched: boolean
  status: string
  notes: string
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString('he-IL', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
  } catch {
    return iso
  }
}

function formatDuration(sec: number) {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function StatCard({label, value, tone}: {label: string; value: string | number; tone?: string}) {
  return (
    <div className='card flex-grow-1' style={{minWidth: 130}}>
      <div className='card-body text-center py-5'>
        <div className={`fs-2hx fw-bold ${tone || ''}`}>{value}</div>
        <div className='text-muted fs-6 mt-1'>{label}</div>
      </div>
    </div>
  )
}

function NumberCard({n}: {n: MyVirtualNumber}) {
  const [forward, setForward] = useState(n.forward_number || '')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(n.forward_number || '')
  const [msg, setMsg] = useState('')

  const save = async () => {
    setSaving(true)
    setMsg('')
    try {
      const res = await axios.patch(`${API_URL}/api/my-virtual-number/${n.ddi}`, {forward_number: forward})
      setSaved(res.data?.forward_number || forward)
      setEditing(false)
      setMsg(res.data?.synced ? 'עודכן ✓' : 'נשמר')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בשמירה')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='card mb-6'>
      <div className='card-body'>
        <div className='d-flex justify-content-between align-items-start flex-wrap mb-6'>
          <div>
            <div className='text-muted fs-7'>המספר הוירטואלי</div>
            <div className='fs-2x fw-bold' dir='ltr'>{n.ddi}</div>
          </div>
          <div className='text-end'>
            <div className='text-muted fs-7'>שיחות מועברות אל</div>
            {editing ? (
              <div className='d-flex gap-2 align-items-center mt-1'>
                <input
                  className='form-control form-control-sm'
                  dir='ltr'
                  style={{width: 150}}
                  value={forward}
                  onChange={(e) => setForward(e.target.value)}
                  placeholder='0501234567'
                />
                <button className='btn btn-sm btn-primary' onClick={save} disabled={saving}>
                  {saving ? '...' : 'שמור'}
                </button>
                <button className='btn btn-sm btn-light' onClick={() => {setEditing(false); setForward(saved)}}>
                  ביטול
                </button>
              </div>
            ) : (
              <div className='d-flex gap-2 align-items-center mt-1'>
                <span className='fs-3 fw-bold' dir='ltr'>{saved || '—'}</span>
                <button className='btn btn-sm btn-light-primary' onClick={() => setEditing(true)}>
                  שנה
                </button>
              </div>
            )}
            {msg && <div className='text-muted fs-8 mt-1'>{msg}</div>}
          </div>
        </div>

        <div className='text-muted fs-7 mb-2'>
          מעקב שיחות ({n.period.from} – {n.period.to})
        </div>
        <div className='d-flex gap-4 flex-wrap mb-6'>
          <StatCard label='סך שיחות' value={n.stats.total} />
          <StatCard label='נענו' value={n.stats.answered} tone='text-success' />
          <StatCard label='לא נענו' value={n.stats.missed} tone='text-danger' />
          <StatCard label='פניות וואטסאפ' value={n.whatsapp_leads ?? 0} tone='text-primary' />
        </div>

        {n.calls && n.calls.length > 0 && (
          <>
            <div className='text-muted fs-7 mb-2'>יומן שיחות</div>
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle gy-2 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted fs-8 text-nowrap'>
                    <th>תאריך</th>
                    <th>מספר מתקשר</th>
                    <th>משך</th>
                    <th>סטטוס</th>
                  </tr>
                </thead>
                <tbody>
                  {n.calls.map((c, i) => (
                    <tr key={i}>
                      <td className='text-nowrap text-muted fs-7'>{formatDate(c.date)}</td>
                      <td className='text-nowrap' dir='ltr'>
                        <a href={`tel:${c.phone.replace(/\D/g, '')}`}>{c.phone}</a>
                      </td>
                      <td className='text-nowrap'>{formatDuration(c.duration)}</td>
                      <td>
                        {c.answered ? (
                          <span className='badge badge-light-success'>נענתה</span>
                        ) : (
                          <span className='badge badge-light-danger'>לא נענתה</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {n.stats.capped && (
              <div className='text-muted fs-8 mt-3'>* ייתכן שמוצגות עד 47 השיחות האחרונות בלבד.</div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ListingModal({lead, onClose}: {lead: WhatsappLead; onClose: () => void}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1090,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className='card'
        dir='rtl'
        style={{maxWidth: 520, width: '100%', maxHeight: '90vh', overflow: 'auto'}}
      >
        <div className='card-header align-items-center'>
          <h3 className='card-title mb-0'>פרטי הנכס</h3>
          <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>
            ✕
          </button>
        </div>
        <div className='card-body'>
          {lead.listing_image && (
            <img
              src={lead.listing_image}
              alt=''
              style={{width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 8, marginBottom: 16}}
            />
          )}
          <div className='d-flex gap-3 align-items-center mb-4'>
            <span className='badge badge-light-success fs-6' dir='ltr'>
              {lead.listing_code}
            </span>
            {lead.listing_price ? <span className='fs-3 fw-bold'>₪{lead.listing_price}</span> : null}
            {lead.listing_deleted && <span className='badge badge-light-danger'>הנכס נמחק</span>}
          </div>
          <div style={{whiteSpace: 'pre-wrap', lineHeight: 1.6}}>{lead.listing_text || 'אין תיאור'}</div>
          <a
            className='btn btn-sm btn-light-primary mt-5'
            href={`https://l.fbzipper.com/${lead.listing_code}`}
            target='_blank'
            rel='noreferrer'
          >
            פתח קישור נכס ↗
          </a>
        </div>
      </div>
    </div>
  )
}

function LeadRow({lead, onOpenListing}: {lead: WhatsappLead; onOpenListing: (l: WhatsappLead) => void}) {
  const [status, setStatus] = useState(lead.status)
  const [notes, setNotes] = useState(lead.notes || '')
  const [savedNotes, setSavedNotes] = useState(lead.notes || '')
  const [busy, setBusy] = useState(false)

  const patch = async (body: {status?: string; notes?: string}) => {
    setBusy(true)
    try {
      const res = await axios.patch(`${API_URL}/api/my-whatsapp-leads/${lead.id}`, body)
      if (typeof res.data?.status === 'string') setStatus(res.data.status)
      if (typeof res.data?.notes === 'string') setSavedNotes(res.data.notes)
    } catch {
      /* ignore — leave UI as-is */
    } finally {
      setBusy(false)
    }
  }

  const handled = status === 'handled'

  return (
    <tr>
      <td className='ps-6 text-nowrap text-muted fs-7'>{formatDate(lead.created_at)}</td>
      <td className='text-nowrap'>{lead.lead_name || '—'}</td>
      <td className='text-nowrap' dir='ltr'>
        <a href={`https://wa.me/${lead.lead_phone.replace(/\D/g, '')}`} target='_blank' rel='noreferrer'>
          {lead.lead_phone}
        </a>
      </td>
      <td style={{minWidth: 200, maxWidth: 260}}>
        {lead.matched ? (
          <div
            className='d-flex align-items-center gap-2'
            style={{cursor: 'pointer'}}
            onClick={() => onOpenListing(lead)}
            title='לחץ לפרטי הנכס'
          >
            {lead.listing_image && (
              <img
                src={lead.listing_image}
                alt=''
                style={{width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0}}
              />
            )}
            <div style={{minWidth: 0}}>
              <span className='badge badge-light-success mb-1' dir='ltr'>{lead.listing_code}</span>
              {lead.listing_deleted && (
                <span className='badge badge-light-danger mb-1 ms-1'>נמחק</span>
              )}
              {lead.listing_text && (
                <div
                  className='text-muted fs-8'
                  style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                  title={lead.listing_text}
                >
                  {lead.listing_text}
                  {lead.listing_price ? ` · ₪${lead.listing_price}` : ''}
                </div>
              )}
            </div>
          </div>
        ) : (
          <span className='badge badge-light-warning'>לא זוהה</span>
        )}
      </td>
      <td className='text-muted' style={{maxWidth: 220, whiteSpace: 'normal'}}>{lead.message}</td>
      <td>
        <button
          className={`btn btn-sm ${handled ? 'btn-success' : 'btn-light-danger'}`}
          disabled={busy}
          onClick={() => patch({status: handled ? 'new' : 'handled'})}
        >
          {handled ? 'טופל ✓' : 'לא טופל'}
        </button>
      </td>
      <td style={{minWidth: 200}}>
        <div className='d-flex gap-2 align-items-center'>
          <input
            className='form-control form-control-sm'
            placeholder='מעקב / הערה…'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {notes !== savedNotes && (
            <button className='btn btn-sm btn-light-primary' disabled={busy} onClick={() => patch({notes})}>
              שמור
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

interface EditablePost {
  id: string
  name: string
  post_content: string
  reply_text: string
  post_code: string
  media_url: string
  category: string
  suggested: string
  suggested_reply: string
  include: boolean
}

interface CampaignLite {
  _id: string
  name: string
  default_signature: string
  default_signature_locked: boolean
}

// Admins keep `admin_auth_token` in localStorage even while impersonating a client;
// send it as x-admin-token so the API's callerIsAdmin() lets them edit a locked
// signature and flip the lock. The server still enforces — this is only the UI gate.
const adminAuthHeaders = (): Record<string, string> => {
  const t = typeof window !== 'undefined' ? localStorage.getItem('admin_auth_token') : null
  return t ? {'x-admin-token': t} : {}
}
const IS_ADMIN = typeof window !== 'undefined' && !!localStorage.getItem('admin_auth_token')

interface ProfileLite {
  _id: string
  name: string
  profile_image: string
  facebook_id: string
  default_signature: string
  default_signature_locked: boolean
  virtual_number_id: string
}

const FB_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

// Small round avatar — prefers the live FB Graph picture (needs the access token),
// falls back to the stored profile_image, then to a neutral initial placeholder.
function Avatar({facebookId, src, name}: {facebookId?: string; src?: string; name: string}) {
  const [failed, setFailed] = useState(false)
  const graph = facebookId && FB_TOKEN ? `https://graph.facebook.com/${facebookId}/picture?type=square&access_token=${FB_TOKEN}` : ''
  const url = graph || src
  return url && !failed ? (
    <img src={url} alt='' onError={() => setFailed(true)} style={{width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}} />
  ) : (
    <span
      className='d-inline-flex align-items-center justify-content-center'
      style={{width: 32, height: 32, borderRadius: '50%', background: '#e4e6ef', fontSize: 13, flexShrink: 0}}
    >
      {(name || '?').trim().charAt(0)}
    </span>
  )
}

// AI "generate signature" button — calls /generate-signature (which reads the
// campaign's posts for context when campaignId is given) and fills the field.
function AiSignatureButton({context, campaignId, onGenerated}: {context?: string; campaignId?: string; onGenerated: (sig: string) => void}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const gen = async () => {
    setBusy(true)
    setErr('')
    try {
      const res = await axios.post(`${API_URL}/generate-signature`, {context: context || '', campaign_id: campaignId || ''})
      if (res.data?.signature) onGenerated(res.data.signature)
      else setErr('לא התקבלה חתימה')
    } catch (e: any) {
      const m = e?.response?.data?.error || ''
      setErr(/credit balance/i.test(m) ? 'אין יתרת קרדיט ב-AI' : m || 'שגיאה ביצירת חתימה')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type='button' className='btn btn-sm btn-light-primary' onClick={gen} disabled={busy}>
        {busy ? 'יוצר…' : '✨ צור עם AI'}
      </button>
      {err && <span className='text-danger fs-8'>{err}</span>}
    </>
  )
}
interface VnOption {
  _id: string
  ddi: string
  enabled: boolean
}

// One profile's signature + DID assignment. Signature overrides the campaign's;
// empty = fall back to the campaign signature. Lock + DID assignment are admin-only.
function ProfileRow({p, numbers, onSaved}: {p: ProfileLite; numbers: VnOption[]; onSaved: (p: ProfileLite) => void}) {
  const [override, setOverride] = useState(!!(p.default_signature || '').trim())
  const [signature, setSignature] = useState(p.default_signature)
  const [savedSig, setSavedSig] = useState(p.default_signature)
  const [locked, setLocked] = useState(p.default_signature_locked)
  const [didId, setDidId] = useState(p.virtual_number_id || '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const readOnly = locked && !IS_ADMIN

  const persist = async (sigValue: string) => {
    setSaving(true)
    setMsg('')
    try {
      const body: any = {profile_id: p._id, default_signature: sigValue}
      if (IS_ADMIN) body.default_signature_locked = locked
      const res = await axios.post(`${API_URL}/update-profile-signature`, body, {headers: adminAuthHeaders()})
      const prof = res.data?.profile
      const newSig = typeof prof?.default_signature === 'string' ? prof.default_signature : sigValue
      const newLock = !!prof?.default_signature_locked
      setSignature(newSig)
      setSavedSig(newSig)
      setLocked(newLock)
      onSaved({...p, default_signature: newSig, default_signature_locked: newLock, virtual_number_id: didId})
      setMsg('נשמר ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה')
    } finally {
      setSaving(false)
    }
  }

  // Toggling the override off clears the stored signature so the bot falls back
  // to the campaign signature; toggling on just reveals the editor.
  const toggleOverride = (checked: boolean) => {
    setOverride(checked)
    if (!checked) {
      setSignature('')
      if (savedSig.trim()) persist('')
    }
  }

  const assignDid = async (value: string) => {
    setDidId(value)
    setMsg('')
    try {
      await axios.post(
        `${API_URL}/assign-profile-virtual-number`,
        {profile_ids: [p._id], virtual_number_id: value || null},
        {headers: adminAuthHeaders()}
      )
      onSaved({...p, virtual_number_id: value, default_signature: savedSig, default_signature_locked: locked})
      setMsg('המספר עודכן ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בהקצאת מספר')
    }
  }

  return (
    <div className='border rounded p-4 mb-3'>
      <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <Avatar facebookId={p.facebook_id} src={p.profile_image} name={p.name} />
          <span className='fw-bold'>{p.name}</span>
        </div>
        <div className='d-flex align-items-center gap-2'>
          {IS_ADMIN && (
            <select className='form-select form-select-sm' style={{width: 210}} value={didId} onChange={(e) => assignDid(e.target.value)}>
              <option value=''>ברירת מחדל (מספר החשבון)</option>
              {numbers.map((n) => (
                <option key={n._id} value={n._id}>{n.ddi}</option>
              ))}
            </select>
          )}
          {IS_ADMIN ? (
            <label className='form-check form-check-sm d-flex align-items-center gap-2 m-0'>
              <input type='checkbox' className='form-check-input' checked={locked} onChange={(e) => setLocked(e.target.checked)} />
              <span className='fs-8'>🔒</span>
            </label>
          ) : locked ? (
            <span className='badge badge-light-warning'>🔒 נעול</span>
          ) : null}
        </div>
      </div>
      <label className='d-flex align-items-center gap-2 mb-2' style={{cursor: readOnly ? 'default' : 'pointer'}}>
        <input
          type='checkbox'
          checked={override}
          disabled={readOnly}
          onChange={(e) => toggleOverride(e.target.checked)}
          style={{width: 16, height: 16, flexShrink: 0, margin: 0}}
        />
        <span className='fs-7 fw-semibold'>חתימה נפרדת לפרופיל זה</span>
        <span className='text-muted fs-8'>— גוברת על חתימת הקמפיין</span>
      </label>
      {override ? (
        <>
          <textarea
            className='form-control form-control-sm'
            rows={3}
            dir='rtl'
            placeholder={'חתימה לפרופיל זה'}
            value={signature}
            readOnly={readOnly}
            onChange={(e) => setSignature(e.target.value)}
          />
          {!readOnly && (
            <div className='d-flex gap-2 align-items-center mt-2 flex-wrap'>
              <button className='btn btn-sm btn-primary' onClick={() => persist(signature)} disabled={saving || signature === savedSig}>
                {saving ? 'שומר…' : 'שמור חתימת פרופיל'}
              </button>
              <AiSignatureButton context={p.name} onGenerated={setSignature} />
              {msg && <span className='text-muted fs-8'>{msg}</span>}
            </div>
          )}
        </>
      ) : (
        <div className='text-muted fs-8'>משתמש בחתימת הקמפיין{msg ? ` · ${msg}` : ''}</div>
      )}
    </div>
  )
}

// Per-profile signatures + DID assignment tab. DID assignment (incl. bulk
// "assign to all") is admin-only; the same DID may be shared across profiles.
function ProfilesPanel() {
  const [profiles, setProfiles] = useState<ProfileLite[]>([])
  const [numbers, setNumbers] = useState<VnOption[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [bulkDid, setBulkDid] = useState('')
  const [bulkMsg, setBulkMsg] = useState('')

  useEffect(() => {
    ;(async () => {
      try {
        const [pRes, nRes] = await Promise.all([
          axios.get(`${API_URL}/my-profiles`),
          axios.get(`${API_URL}/my-virtual-numbers`).catch(() => null),
        ])
        setProfiles(pRes.data?.profiles || [])
        setNumbers(nRes?.data?.numbers || [])
      } catch (e: any) {
        setMsg(e?.response?.data?.error || 'שגיאה בטעינת הפרופילים')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const updateProfile = (up: ProfileLite) => setProfiles((prev) => prev.map((x) => (x._id === up._id ? up : x)))

  const assignAll = async () => {
    setBulkMsg('')
    try {
      const res = await axios.post(
        `${API_URL}/assign-profile-virtual-number`,
        {all: true, virtual_number_id: bulkDid || null},
        {headers: adminAuthHeaders()}
      )
      setProfiles((prev) => prev.map((x) => ({...x, virtual_number_id: bulkDid})))
      setBulkMsg(`עודכנו ${res.data?.modified ?? 0} פרופילים`)
    } catch (e: any) {
      setBulkMsg(e?.response?.data?.error || 'שגיאה')
    }
  }

  if (loading) return <div className='text-muted py-10 text-center'>טוען פרופילים…</div>
  if (msg) return <div className='alert alert-danger'>{msg}</div>

  return (
    <>
      {IS_ADMIN && (
        <div className='border rounded p-3 mb-4 bg-light-warning d-flex align-items-center gap-2 flex-wrap'>
          <span className='fs-8 fw-bold'>הקצאת מספר לכל פרופילי ההורים:</span>
          <select className='form-select form-select-sm' style={{width: 210}} value={bulkDid} onChange={(e) => setBulkDid(e.target.value)}>
            <option value=''>ברירת מחדל (מספר החשבון)</option>
            {numbers.map((n) => (
              <option key={n._id} value={n._id}>{n.ddi}</option>
            ))}
          </select>
          <button className='btn btn-sm btn-warning' onClick={assignAll}>הקצה לכולם</button>
          {bulkMsg && <span className='text-muted fs-8'>{bulkMsg}</span>}
        </div>
      )}
      {IS_ADMIN && (
        <div className='fs-8 text-muted mb-2'>פרופיל ללא מספר משתמש במספר ברירת המחדל של החשבון. שייכו לחשבון כמה מספרים כדי לתת לכל פרופיל מספר אחר.</div>
      )}
      <div className='fs-8 text-muted mb-3'>חתימת פרופיל גוברת על חתימת הקמפיין. השאר ריק כדי להשתמש בחתימת הקמפיין.</div>
      {profiles.length === 0 ? (
        <div className='text-muted py-10 text-center'>לא נמצאו פרופילים.</div>
      ) : (
        profiles.map((p) => <ProfileRow key={p._id} p={p} numbers={numbers} onSaved={updateProfile} />)
      )}
    </>
  )
}

// One campaign's default-signature editor (AI-generate + admin lock). Shown for
// every campaign together in the "campaigns" tab.
function CampaignSignatureCard({c}: {c: CampaignLite}) {
  const [sig, setSig] = useState(c.default_signature)
  const [saved, setSaved] = useState(c.default_signature)
  const [locked, setLocked] = useState(c.default_signature_locked)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const readOnly = locked && !IS_ADMIN
  const save = async () => {
    setSaving(true)
    setMsg('')
    try {
      const body: any = {campaign_id: c._id, name: c.name, default_signature: sig}
      if (IS_ADMIN) body.default_signature_locked = locked
      const res = await axios.post(`${API_URL}/update-campaign`, body, {headers: adminAuthHeaders()})
      const camp = res.data?.campaign
      const newSig = typeof camp?.default_signature === 'string' ? camp.default_signature : sig
      const newLock = !!camp?.default_signature_locked
      setSig(newSig)
      setSaved(newSig)
      setLocked(newLock)
      setMsg('נשמר ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה')
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className='border rounded p-4 mb-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <span className='fw-bold'>{c.name}</span>
        {IS_ADMIN ? (
          <label className='form-check form-check-sm d-flex align-items-center gap-2 m-0'>
            <input type='checkbox' className='form-check-input' checked={locked} onChange={(e) => setLocked(e.target.checked)} />
            <span className='fs-8'>🔒 נעל ללקוח</span>
          </label>
        ) : locked ? (
          <span className='badge badge-light-warning'>🔒 נעול</span>
        ) : null}
      </div>
      <textarea
        className='form-control form-control-sm'
        rows={3}
        dir='rtl'
        placeholder={'לדוגמה: לפרטים וליצירת קשר בוואטסאפ 📞 {number} · 💬 {wa_link}'}
        value={sig}
        readOnly={readOnly}
        onChange={(e) => setSig(e.target.value)}
      />
      {!readOnly && (
        <div className='d-flex gap-2 align-items-center mt-2 flex-wrap'>
          <button className='btn btn-sm btn-primary' onClick={save} disabled={saving || (sig === saved && locked === c.default_signature_locked)}>
            {saving ? 'שומר…' : 'שמור חתימה'}
          </button>
          <AiSignatureButton context={c.name} campaignId={c._id} onGenerated={setSig} />
          {msg && <span className='text-muted fs-8'>{msg}</span>}
        </div>
      )}
      {readOnly && <div className='text-muted fs-8 mt-1'>החתימה נעולה על ידי מנהל.</div>}
    </div>
  )
}

// Virtual-number setup modal: all campaign signatures together, per-profile
// signatures + DID assignment, and a one-time AI strip of the old contact info.
function BulkContactEditor({onClose}: {onClose: () => void}) {
  const [campaigns, setCampaigns] = useState<CampaignLite[]>([])
  const [vn, setVn] = useState('')
  const [loadMsg, setLoadMsg] = useState('')
  // Clients get ONE step: replace. Signature editing + reverse-by-date are admin-only.
  const [tab, setTab] = useState<'replace' | 'signatures' | 'reverse'>('replace')

  // replace tab (one-step: bake the virtual number + tracked link into every post)
  const [posts, setPosts] = useState<EditablePost[]>([])
  const [postsLoaded, setPostsLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    ;(async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          axios.get(`${API_URL}/my-campaigns`),
          axios.get(`${API_URL}/api/my-posts`).catch(() => null),
        ])
        setCampaigns(cRes.data?.campaigns || [])
        setVn(pRes?.data?.virtual_number || '')
      } catch (e: any) {
        setLoadMsg(e?.response?.data?.error || 'שגיאה בטעינת הקמפיינים')
      }
    })()
  }, [])

  const loadAllPosts = async () => {
    setLoading(true)
    setMsg('')
    try {
      const res = await axios.get(`${API_URL}/api/my-posts`)
      setPosts(
        (res.data?.posts || []).map((p: any) => ({...p, suggested: '', suggested_reply: '', include: true}))
      )
      setPostsLoaded(true)
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בטעינת המודעות')
    } finally {
      setLoading(false)
    }
  }

  // Load all posts (across every campaign) the first time the replace tab opens.
  useEffect(() => {
    if (tab === 'replace' && !postsLoaded && !loading) loadAllPosts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const suggestAll = async () => {
    setSuggesting(true)
    setMsg('מחליף פרטי קשר עם AI… זה עשוי לקחת זמן')
    try {
      const res = await axios.post(`${API_URL}/api/my-posts/suggest`, {})
      const byId: Record<string, {suggested?: string}> = {}
      ;(res.data?.suggestions || []).forEach((s: any) => {
        byId[s.id] = {suggested: s.suggested}
      })
      setPosts((prev) => prev.map((p) => (byId[p.id] ? {...p, suggested: byId[p.id].suggested || p.suggested} : p)))
      setMsg('פרטי הקשר הוחלפו — בדקו, ערכו ושמרו')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בהחלפת הפרטים')
    } finally {
      setSuggesting(false)
    }
  }

  const saveAll = async () => {
    const updates = posts
      .filter((p) => p.include)
      .map((p) => {
        const u: {post_id: string; post_content?: string} = {post_id: p.id}
        if (p.suggested && p.suggested !== p.post_content) u.post_content = p.suggested
        return u
      })
      .filter((u) => u.post_content !== undefined)
    if (!updates.length) {
      setMsg('אין שינויים לשמירה')
      return
    }
    setSaving(true)
    try {
      const res = await axios.post(`${API_URL}/api/my-posts/bulk-update`, {updates})
      const saved = res.data?.saved || 0
      setMsg(`נשמרו ${saved} מודעות (הגרסה הקודמת נשמרה בהיסטוריה וניתנת לשחזור לפי תאריך)`)
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בשמירה')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1090,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className='card'
        dir='rtl'
        style={{maxWidth: 820, width: '100%', margin: 'auto'}}
      >
        <div className='card-header align-items-center'>
          <div>
            <h3 className='card-title mb-0'>מספר וירטואלי — פרטי קשר</h3>
            {vn && <div className='text-muted fs-8 mt-1' dir='ltr'>{vn}</div>}
          </div>
          <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>✕</button>
        </div>
        <div className='card-body'>
          <ul className='nav nav-tabs nav-line-tabs mb-4 gap-8'>
            <li className='nav-item'>
              <a className={`nav-link ${tab === 'replace' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('replace')}>
                החלפת פרטי קשר
              </a>
            </li>
            {IS_ADMIN && (
              <li className='nav-item'>
                <a className={`nav-link ${tab === 'signatures' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('signatures')}>
                  חתימות <span className='badge badge-light-danger fs-9 ms-1'>אדמין</span>
                </a>
              </li>
            )}
            {IS_ADMIN && (
              <li className='nav-item'>
                <a className={`nav-link ${tab === 'reverse' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('reverse')}>
                  שחזור לפי תאריך <span className='badge badge-light-danger fs-9 ms-1'>אדמין</span>
                </a>
              </li>
            )}
          </ul>

          {loadMsg && <div className='alert alert-danger'>{loadMsg}</div>}

          {tab === 'signatures' && IS_ADMIN && (
            <>
              <div className='fs-8 text-muted mb-3'>
                החתימה משמשת כתבנית לבלוק פרטי הקשר שיוטמע במודעות שאין בהן פרטי קשר. ניתן להשתמש ב־<code dir='ltr'>{'{number}'}</code> וב־<code dir='ltr'>{'{wa_link}'}</code>.
              </div>
              {campaigns.length === 0 ? (
                <div className='text-muted py-6 text-center'>לא נמצאו קמפיינים.</div>
              ) : (
                campaigns.map((c) => <CampaignSignatureCard key={c._id} c={c} />)
              )}
              <h4 className='mt-6 mb-3'>פרופילים</h4>
              <ProfilesPanel />
            </>
          )}

          {tab === 'reverse' && IS_ADMIN && <ReverseByDatePanel />}

          {tab === 'replace' && (
            <>
              <div className='d-flex gap-2 align-items-center mb-3 flex-wrap'>
                <button className='btn btn-sm btn-primary' onClick={suggestAll} disabled={suggesting || loading || !posts.length}>
                  {suggesting ? 'מחליף…' : '🔁 החלף פרטי קשר במספר הווירטואלי'}
                </button>
                <button className='btn btn-sm btn-success' onClick={saveAll} disabled={saving || suggesting || !posts.length}>
                  {saving ? 'שומר…' : '💾 שמור הכל'}
                </button>
                {msg && <span className='text-muted fs-7'>{msg}</span>}
              </div>
              <div className='fs-8 text-muted mb-4'>
                המערכת מחליפה את מספר הטלפון ואת קישור הוואטסאפ במספר הווירטואלי ובקישור מעקב, ומשאירה את המודעה זהה ככל האפשר. אם אין פרטי קשר במודעה — הם יתווספו במקום הגיוני.
              </div>

              {loading ? (
                <div className='text-muted py-10 text-center'>טוען מודעות…</div>
              ) : posts.length === 0 ? (
                <div className='text-muted py-10 text-center'>לא נמצאו מודעות.</div>
              ) : (
                posts.map((p) => (
                  <div className='border rounded p-4 mb-4' key={p.id}>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <label className='form-check form-check-sm d-flex align-items-center gap-2 m-0'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={p.include}
                          onChange={(e) => setPosts((prev) => prev.map((x) => (x.id === p.id ? {...x, include: e.target.checked} : x)))}
                        />
                        <span className='fw-bold'>{p.name || 'מודעה'}</span>
                      </label>
                      {p.post_code && <span className='badge badge-light-success' dir='ltr'>{p.post_code}</span>}
                    </div>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <div className='text-muted fs-8 mb-1'>מקורי</div>
                        <div
                          className='text-muted fs-8 p-2 bg-light rounded'
                          style={{whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto'}}
                        >
                          {p.post_content || '—'}
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className='text-muted fs-8 mb-1'>לאחר החלפה — כפי שיתפרסם (ניתן לעריכה)</div>
                        <textarea
                          className='form-control form-control-sm'
                          rows={9}
                          placeholder='לחץ "החלף פרטי קשר" למעלה'
                          value={p.suggested}
                          onChange={(e) => setPosts((prev) => prev.map((x) => (x.id === p.id ? {...x, suggested: e.target.value} : x)))}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}


export function VirtualNumberWrapper() {
  const [numbers, setNumbers] = useState<MyVirtualNumber[]>([])
  const [leads, setLeads] = useState<WhatsappLead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [modalLead, setModalLead] = useState<WhatsappLead | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const load = async (fromArg?: string, toArg?: string) => {
    try {
      const qs = fromArg && toArg ? `?from=${fromArg}&to=${toArg}` : ''
      const [numRes, leadRes] = await Promise.all([
        axios.get(`${API_URL}/api/my-virtual-number${qs}`),
        axios.get(`${API_URL}/api/my-whatsapp-leads`).catch(() => null),
      ])
      setNumbers(numRes.data?.numbers || [])
      setLeads(leadRes?.data?.leads || [])
      // Seed the date inputs from the server's resolved period on first load.
      if (numRes.data?.period && (!fromArg || !toArg)) {
        setFrom(numRes.data.period.from)
        setTo(numRes.data.period.to)
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  if (loading) return <div className='p-10 text-muted' dir='rtl'>טוען…</div>

  return (
    <div className='p-6' dir='rtl' style={{maxWidth: 980, margin: '0 auto'}}>
      <h2 className='mb-6'>המספר הוירטואלי שלי</h2>

      {error && <div className='alert alert-danger'>{error}</div>}

      {numbers.length > 0 && (
        <div className='d-flex gap-3 align-items-end mb-6 flex-wrap'>
          <div>
            <label className='form-label fs-8 text-muted mb-1'>מתאריך</label>
            <input type='date' className='form-control form-control-sm' value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className='form-label fs-8 text-muted mb-1'>עד תאריך</label>
            <input type='date' className='form-control form-control-sm' value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button className='btn btn-sm btn-light-primary' onClick={() => load(from, to)}>עדכן</button>
        </div>
      )}

      {numbers.length === 0 ? (
        <div className='card'>
          <div className='card-body text-center text-muted py-15'>
            לא הוקצה לך מספר וירטואלי. לפרטים נוספים פנה לתמיכה.
          </div>
        </div>
      ) : (
        numbers.map((n) => <NumberCard key={n.ddi} n={n} />)
      )}

      {numbers.length > 0 && (
        <div className='mb-6'>
          <button className='btn btn-primary' onClick={() => setShowEditor(true)}>
            ⚙️ החלפת פרטי קשר במספר וירטואלי{IS_ADMIN ? ' / שחזור לפי תאריך' : ''}
          </button>
        </div>
      )}

      {leads.length > 0 && (
        <div className='card mb-6'>
          <div className='card-header align-items-center'>
            <h3 className='card-title mb-0'>לידים מוואטסאפ</h3>
            <span className='badge badge-light-primary ms-2'>{leads.length}</span>
          </div>
          <div className='card-body p-0'>
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle gy-3 mb-0'>
                <thead>
                  <tr className='fw-bold text-muted fs-7 text-nowrap'>
                    <th className='ps-6'>תאריך</th>
                    <th>שם</th>
                    <th>טלפון</th>
                    <th>נכס</th>
                    <th>הודעה</th>
                    <th>סטטוס</th>
                    <th>מעקב</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <LeadRow key={l.id} lead={l} onOpenListing={setModalLead} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {modalLead && <ListingModal lead={modalLead} onClose={() => setModalLead(null)} />}
      {showEditor && <BulkContactEditor onClose={() => setShowEditor(false)} />}
    </div>
  )
}

export default VirtualNumberWrapper
